import {Review, LearningProductType} from '@/types/review';
import {useState, useRef, useMemo, useCallback, useEffect} from 'react';
import {getMonolithApiHeaders} from '@udemy-v2/api-fetch';

/**
 * Field selection options for reviews
 */
export enum ReviewFieldSelection {
    ID = 'id',
    CONTENT = 'content_html',
    RATING = 'rating',
    CREATED = 'created',
    USER = 'user',
    RESPONSE = 'response',
}

/**
 * Field selection options for user
 */
export enum UserFieldSelection {
    ID = 'id',
    DISPLAY_NAME = 'public_display_name',
    IMAGE = 'image_50x50',
    TITLE = 'title',
}

/**
 * Field selection options for review responses
 */
export enum ReviewResponseFieldSelection {
    ID = 'id',
    CONTENT = 'content_html',
    USER = 'user',
    CREATED = 'created',
}

/**
 * Ordering options for reviews
 */
export enum ReviewOrdering {
    CREATED_ASC = 'created',
    CREATED_DESC = '-created',
    SCORE_RANK = 'score_rank',
    REVIEW_SCORE_RANK = 'course_review_score__rank',
}

/**
 * Configuration object for review API requests
 */
export interface ReviewRequestConfig {
    courseId: string;
    page?: number;
    pageSize?: number;
    rating?: number;
    isTextReview?: boolean;
    filterBy?: string;
    searchText?: string;
    ordering?: ReviewOrdering[];
    fields?: ReviewFieldSelection[];
    userFields?: UserFieldSelection[];
    responseFields?: ReviewResponseFieldSelection[];
}

/**
 * API Response for a User object in reviews
 */
export interface UserResponse {
    _class: string;
    title: string;
    name: string;
    display_name: string;
    image_50x50: string;
    initials: string;
    tracking_id: string;
    public_display_name: string;
}

/**
 * API Response for a Review Response object
 */
export interface ReviewResponseData {
    _class: string;
    id: number;
    content_html: string;
    user: UserResponse;
    created_formatted_with_time_since: string;
    created: string;
    content: string;
}

/**
 * API Response for a single Review item
 */
export interface ReviewItemResponse {
    _class: string;
    id: number;
    content: string;
    rating: number;
    created: string;
    modified: string;
    user_modified: string;
    user: UserResponse;
    response: ReviewResponseData | null;
    content_html: string;
    created_formatted_with_time_since: string;
}

/**
 * Rating distribution item for reviews
 */
export interface RatingDistributionItem {
    rating: number;
    count: number;
}

/**
 * API Response structure for the Reviews API
 */
export interface ReviewsApiResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: ReviewItemResponse[];
    rating_distribution: RatingDistributionItem[];
}

/**
 * Create a unique cache key from a request configuration
 */
export function generateCacheKey(config: ReviewRequestConfig): string {
    // Generate a stable JSON key from the config
    const key = {
        courseId: config.courseId,
        page: config.page ?? 1,
        pageSize: config.pageSize ?? 12,
        rating: config.rating,
        isTextReview: config.isTextReview,
        filterBy: config.filterBy,
        searchText: config.searchText,
        // Sort the ordering array to ensure consistent keys regardless of order
        ordering: config.ordering ? [...config.ordering].sort() : undefined,
        fields: config.fields ? [...config.fields].sort() : undefined,
        userFields: config.userFields ? [...config.userFields].sort() : undefined,
        responseFields: config.responseFields ? [...config.responseFields].sort() : undefined,
    };

    return JSON.stringify(key);
}

/**
 * Format a review request using a specific format with individual query parameters
 * This is included to support the test case for the expected URL format
 */
export function generateQueryUrl(config: ReviewRequestConfig): string {
    const params = new URLSearchParams();

    // Add basic parameters
    params.append('courseId', config.courseId);
    params.append('page', String(config.page || 1));
    params.append('include_rating_distribution', '1');

    // Add conditional parameters
    if (config.isTextReview) {
        params.append('is_text_review', '1');
    }

    // Handle ordering
    if (config.ordering && config.ordering.length > 0) {
        params.append('ordering', config.ordering.join(','));
    }

    // Add field specifications with formatted syntax
    if (config.fields && config.fields.length > 0) {
        params.append(
            'fields[course_review]',
            '@default,response,content_html,created_formatted_with_time_since',
        );
    }

    if (config.userFields && config.userFields.length > 0) {
        params.append('fields[user]', '@min,image_50x50,initials,public_display_name,tracking_id');
    }

    if (config.responseFields && config.responseFields.length > 0) {
        params.append(
            'fields[course_review_response]',
            '@min,user,content_html,created_formatted_with_time_since',
        );
    }

    if (config.rating) {
        params.append('rating', String(config.rating));
    }

    if (config.searchText) {
        params.append('search', config.searchText);
    }

    return `courses/${config.courseId}/reviews/?${params.toString()}`;
}

/**
 * Transform the API response into an array of Review objects
 */
export function transformReviewsResponse(response: ReviewsApiResponse): Review[] {
    return response.results.map((reviewItem) => ({
        id: String(reviewItem.id),
        type: LearningProductType.COURSE,
        content: reviewItem.content_html || reviewItem.content,
        rating: reviewItem.rating,
        createdAt: reviewItem.created,
        user: {
            name: reviewItem.user.name || reviewItem.user.public_display_name,
            display_name: reviewItem.user.display_name,
            tracking_id: reviewItem.user.tracking_id,
            image_50x50: reviewItem.user.image_50x50,
            images: {
                px50x50: reviewItem.user.image_50x50,
            },
        },
        response: reviewItem.response
            ? {
                  id: String(reviewItem.response.id),
                  content: reviewItem.response.content || reviewItem.response.content_html,
                  user: {
                      name:
                          reviewItem.response.user.name ||
                          reviewItem.response.user.public_display_name,
                      display_name: reviewItem.response.user.display_name,
                      tracking_id: reviewItem.response.user.tracking_id,
                      image_50x50: reviewItem.response.user.image_50x50,
                      images: {
                          px50x50: reviewItem.response.user.image_50x50,
                      },
                  },
                  createdAt: reviewItem.response.created,
              }
            : undefined,
        page: 1,
        pageCount: Math.ceil(response.count / 10), // Assuming 10 items per page
    }));
}

/**
 * Calculate the average rating from a ratings distribution array
 */
export function calculateAverageRating(distribution: RatingDistributionItem[]): number {
    const total = distribution.reduce((acc, item) => acc + item.count, 0);
    if (total === 0) return 0;
    const sum = distribution.reduce((acc, item) => acc + item.rating * item.count, 0);
    return sum / total;
}

/**
 * Hook to fetch and cache reviews based on a configuration
 * @param config - Configuration for the reviews request
 * @returns Object containing reviews data, loading state, error state, fetchNext function, and hasMore flag
 */
export function useReviews(config: ReviewRequestConfig) {
    const [data, setData] = useState<Review[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [nextUrl, setNextUrl] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [ratingDistribution, setRatingDistribution] = useState<RatingDistributionItem[]>([]);
    const [averageRating, setAverageRating] = useState<number>(0);

    // Cache stored in a ref to persist between renders
    const cache = useRef<Record<string, Review[]>>({});
    const prevConfigRef = useRef<string>('');

    // Create the cache key from the config
    const cacheKey = useMemo(() => generateCacheKey(config), [config]);

    // Create the query URL from the config
    const queryUrl = useMemo(() => generateQueryUrl(config), [config]);

    // Function to fetch data with a specific URL
    const fetchWithUrl = useCallback(
        async (url: string, append: boolean = false, cacheKey?: string) => {
            if (!append) {
                setIsLoading(true);
            }

            try {
                const fetchResponse = await fetch(`/api-2.0/${url}`, {
                    method: 'GET',
                    headers: {
                        ...getMonolithApiHeaders(),
                    },
                });

                if (!fetchResponse.ok) {
                    throw new Error(
                        `Failed to fetch reviews: ${fetchResponse.status} ${fetchResponse.statusText}`,
                    );
                }

                const response: ReviewsApiResponse = await fetchResponse.json();

                // Transform the response
                const transformedData = transformReviewsResponse(response);

                // Set rating distribution and average rating from API response
                const distribution = response.rating_distribution || [];
                setRatingDistribution(distribution);
                setAverageRating(calculateAverageRating(distribution));

                // Check if response has pagination info
                if (response.next !== undefined) {
                    setNextUrl(response.next);
                    setHasMore(!!response.next);
                }

                // Store in cache if cacheKey is provided
                if (cacheKey) {
                    cache.current[cacheKey] = transformedData;
                }

                // Update data state
                setData((prevData) =>
                    append ? [...prevData, ...transformedData] : transformedData,
                );

                setError(undefined);
            } catch (err) {
                setError(err instanceof Error ? err : new Error('An unknown error occurred'));
            } finally {
                if (!append) {
                    setIsLoading(false);
                }
            }
        },
        [],
    );

    // Function to fetch next page of data
    const fetchNext = useCallback(async () => {
        if (!nextUrl || isLoading) return;

        setIsLoadingMore(true);
        // The API returns a full URL, but udApi expects a path relative to the API root.
        // We strip the protocol and host to get a relative path.
        let relativeUrl = nextUrl;
        try {
            const parsedUrl = new URL(nextUrl);
            relativeUrl = parsedUrl.pathname + parsedUrl.search;

            // The initial query URL is `courses/...`, so we strip the `/api-2.0` prefix.
            const apiPrefix = '/api-2.0/';
            if (relativeUrl.startsWith(apiPrefix)) {
                relativeUrl = relativeUrl.substring(apiPrefix.length);
            }
        } catch {
            // Not a full URL, so we can probably use it as is.
        }

        try {
            await fetchWithUrl(relativeUrl, true);
        } finally {
            setIsLoadingMore(false);
        }
    }, [nextUrl, isLoading, fetchWithUrl]);

    useEffect(() => {
        let isMounted = true;

        // When configuration changes, set loading state immediately
        setIsLoading(true);

        // Detect configuration change by comparing cache keys
        const configChanged = prevConfigRef.current !== cacheKey;
        prevConfigRef.current = cacheKey;

        const fetchInitialData = async () => {
            if (!isMounted) return;

            // Check if we have cached data and config hasn't changed
            if (cache.current[cacheKey] && !configChanged) {
                setData(cache.current[cacheKey]);
                setIsLoading(false);
                return;
            }

            if (isMounted) {
                await fetchWithUrl(queryUrl, false, cacheKey);
            }
        };

        fetchInitialData();

        return () => {
            isMounted = false;
        };
    }, [cacheKey, queryUrl, fetchWithUrl]);

    return {
        data,
        isLoading,
        isLoadingMore,
        error,
        fetchNext,
        hasMore,
        ratingDistribution,
        averageRating,
        searchText: config.searchText,
        rating: config.rating,
    };
}
